// @flow

import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import {
  VisuallySimilarIcon,
  FreeReturnIcon,
  FiltersIcon,
  CustomerIcon,
  ReviewServiceIcon,
  SaveWishlistIcon,
} from '@riseart/icons';
import { Heading, Paragraph, Link, Button } from '@riseart/common';
import { stringifyHash } from '@riseart/fe-utils';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { URL_PARTS, UrlTranslator } from 'shared_services/riseart/url/Translator';
import {
  gtmPromoCardShow,
  gtmPromoCardClick,
} from 'shared_data/providers/google/tagmanager/Actions';
import {
  promotionalCardCls,
  promotionalCardTitleCls,
  promotionalCardTextCls,
  promotionalCardIconCls,
  promotionalCardLinkCls,
} from 'shared_components/messages/PromotionalCard.less';

/**
 * getHrefBySource
 *
 * @param {Object} href
 * @returns
 */
function getHrefBySource(href: Object): string {
  switch (href.source) {
    case 'config':
      return LocalizedConfig.get(href.key);
    case 'hash':
      return stringifyHash(UrlTranslator.translateTo(href.key, URL_PARTS.HASH));
    case 'urlAssembler':
      return UrlAssembler.byRouteKey(href.key, href.options);
    case 'link':
    default: {
      return href.key;
    }
  }
}

type Props = {
  code: string,
  location: string,
  listPosition: number,
  componentProps: Object,
};

/**
 * PromotionalCardMessage
 *
 * @param {Props} props
 */
export function PromotionalCardMessage(item: Props) {
  const { title, text, icon, link } = item.componentProps;

  const ICONS = {
    visuallySimilar: VisuallySimilarIcon,
    filters: FiltersIcon,
    freeReturn: FreeReturnIcon,
    customer: CustomerIcon,
    reviews: ReviewServiceIcon,
    saveWishlist: SaveWishlistIcon,
  };
  const TRANSLATION_PLACEHOLDERS = {
    visualSimilarIcon: <VisuallySimilarIcon />,
    br: () => <br />,
  };
  const IconComponent = (icon && ICONS[icon]) || null;
  const dispatch = useDispatch();
  const [wasVisible, setWasVisible] = useState(null);
  const handleVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (isVisible && wasVisible === null) {
        dispatch(
          gtmPromoCardShow({
            code: item.code,
            location: item.location,
            position: item.listPosition,
          }),
        );
        setWasVisible(true);
      }
    },
    // eslint-disable-next-line
    [wasVisible, item.code],
  );
  const handleClick = useCallback(() => {
    dispatch(
      gtmPromoCardClick({
        code: item.code,
        location: item.location,
        position: item.listPosition,
      }),
    );
    // eslint-disable-next-line
  }, [item.code]);

  const linkProps = {
    to: getHrefBySource(link.href),
    external: !!link.external,
    ...(link.target ? { target: link.target } : null),
    onClick: handleClick,
  };

  return (
    <VisibilitySensor onChange={handleVisibilityChange}>
      <div className={promotionalCardCls}>
        {IconComponent ? (
          <div className={promotionalCardIconCls}>
            <IconComponent />
          </div>
        ) : null}
        {title ? (
          <Heading tag="h5" level="3" className={promotionalCardTitleCls}>
            <FormattedMessage id={title} values={TRANSLATION_PLACEHOLDERS}>
              {(headingText: string) => headingText}
            </FormattedMessage>
          </Heading>
        ) : null}

        {text ? (
          <Paragraph className={promotionalCardTextCls}>
            <FormattedMessage id={text} values={TRANSLATION_PLACEHOLDERS}>
              {(noItemsText: string) => noItemsText}
            </FormattedMessage>
          </Paragraph>
        ) : null}
        {link ? (
          <div className={promotionalCardLinkCls}>
            <FormattedMessage id={link.text}>
              {(linkText: string) =>
                link.type === 'link' ? (
                  <Link {...linkProps} title={linkText}>
                    {linkText}
                  </Link>
                ) : (
                  <Button isAnchor type="secondary" size="medium" {...linkProps} title={linkText}>
                    {linkText}
                  </Button>
                )
              }
            </FormattedMessage>
          </div>
        ) : null}
      </div>
    </VisibilitySensor>
  );
}
